import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { blockDoesNotSupportBenchmark, type StudioAnalysisRequest } from 'venn-state';
import { useAnalysis, useResponseParser } from '../../../studio-blocks/logic/useAnalysis';
import { filterAnalyses, filterRequests } from '..';
import type { Analysis } from 'venn-api';

/**
 * Gets both the filtered requests and analyses for a block
 * Note that this hook suspends while analyses are loading
 * @param id id of the block
 */
export const useFilteredRequestsAndAnalyses = (
  id: string,
): { requests: StudioAnalysisRequest[]; analyses: (Analysis | undefined)[][] } => {
  const { requests, analyses } = useAnalysis(id);
  const hideBenchmark = useRecoilValue(blockDoesNotSupportBenchmark(id));
  const responseParser = useResponseParser(id);
  const isTreeBlock = !!responseParser.isTree;

  const { requests: filteredRequests, analyses: filteredAnalyses } = useMemo(
    () =>
      requests.length === analyses.length
        ? {
            requests: filterRequests(requests, isTreeBlock, hideBenchmark),
            analyses: filterAnalyses(requests, analyses, isTreeBlock, hideBenchmark),
          }
        : {
            requests,
            analyses,
          },
    [analyses, hideBenchmark, requests, isTreeBlock],
  );

  return { requests: filteredRequests, analyses: filteredAnalyses };
};
